import { useEffect, useMemo, useRef, useState } from "react";
import type { Location } from "../lib/types";
import { geoReverse, geoSearch } from "../lib/api";
import { calibrateLocation } from "../lib/locationCalibration";
import { useLocale } from "../lib/i18n";

export function CityPicker(props: {
  value: Location | null;
  onChange: (loc: Location) => void;
}) {
  const { t } = useLocale();
  const [q, setQ] = useState("");
  const [results, setResults] = useState<Location[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement | null>(null);

  const display = useMemo(() => {
    if (!props.value) return t.selectCityDots;
    const admin = props.value.admin ? ` · ${props.value.admin}` : "";
    return `${props.value.city}, ${props.value.country}${admin}`;
  }, [props.value, t]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!boxRef.current) return;
      if (boxRef.current.contains(e.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  async function runSearch(rawText: string) {
    const text = rawText.trim();
    if (busy || text.length < 2) return;

    setBusy(true);
    setError(null);
    try {
      const data = await geoSearch(text);
      const list = (data.results ?? []).map((loc) => calibrateLocation(loc));
      setResults(list);
      setOpen(true);
    } catch (e: any) {
      setError(e?.message ?? t.searchFailed);
      setResults([]);
    } finally {
      setBusy(false);
    }
  }

  async function handleUseMyLocation() {
    if (busy || !("geolocation" in navigator)) return;

    setBusy(true);
    setError(null);
    try {
      const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 9000,
          maximumAge: 60_000
        });
      });
      const preferredTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const loc = await geoReverse(pos.coords.latitude, pos.coords.longitude, preferredTimeZone);
      props.onChange(calibrateLocation(loc, preferredTimeZone));
      setOpen(false);
      setQ("");
      setResults([]);
    } catch (e: any) {
      setError(e?.message ?? t.couldNotGetLocation);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div ref={boxRef} style={{ position: "relative" }}>
      <div className="row" style={{ gap: 10, alignItems: "stretch" }}>
        <div style={{ flex: 1 }}>
          <input
            className="input"
            value={q}
            placeholder={display}
            onFocus={() => setOpen(true)}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void runSearch(q);
            }}
            aria-label={t.searchCityAria}
          />
        </div>
        <button className="btn" onClick={() => void runSearch(q)} aria-disabled={busy || q.trim().length < 2} disabled={busy || q.trim().length < 2}>
          {busy ? t.searching : t.search}
        </button>
        <button className="btn" onClick={() => void handleUseMyLocation()} aria-disabled={busy || !("geolocation" in navigator)} disabled={busy || !("geolocation" in navigator)}>
          {t.useMyLocation}
        </button>
      </div>

      {error && <div style={{ color: "var(--danger)", fontSize: 12, marginTop: 8 }}>{error}</div>}

      {open && results.length > 0 && (
        <div
          className="card"
          style={{
            position: "absolute",
            marginTop: 10,
            width: "100%",
            padding: 10,
            zIndex: 20
          }}
          role="listbox"
        >
          <div className="p" style={{ fontSize: 12, marginBottom: 8 }}>
            {t.selectMatch}
          </div>
          <div style={{ maxHeight: 320, overflow: "auto" }}>
            {results.map((r, idx) => (
              <button
                key={`${r.city}-${r.country}-${idx}`}
                className="btn"
                style={{
                  width: "100%",
                  justifyContent: "space-between",
                  borderRadius: 12,
                  padding: "10px 12px",
                  marginBottom: 8
                }}
                onClick={() => {
                  props.onChange(calibrateLocation(r));
                  setOpen(false);
                  setQ("");
                  setResults([]);
                }}
              >
                <span style={{ textAlign: "left" }}>
                  <strong>{r.city}</strong>, {r.country}
                  {r.admin ? <span style={{ color: "var(--muted)" }}> · {r.admin}</span> : null}
                </span>
                <span className="chip soft">{r.timezone}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
